import { useMemo, useRef, useEffect } from 'react';
import { processQuestionHtmlForRender, extractImageUrls } from '../utils/questionHtmlProcessor';
import { watchImageHeight } from '../utils/imageHeightLimit';

interface QuestionHtmlWithZoomProps {
  html: string;
  imageWidths?: number[];
  onWidthChange?: (newWidths: number[]) => void;
  isPreview?: boolean;
  /**
   * @deprecated 历史遗留参数，不再支持图片缩放交互。
   * quiz 模式现在纯展示，与编辑器 1:1 复刻。
   */
  questionType?: string;
}

/**
 * 题目 HTML 渲染组件（纯展示）
 *
 * 设计目标：与编辑模式（QuillEditorWithImageResize）1:1 复刻渲染效果。
 * 两端共用 processQuestionHtmlForRender 函数处理 HTML，确保：
 * - 图片 URL 规范化一致
 * - 空段落清理一致
 * - 连续 <br> 压缩一致
 * - 图文分行规则一致
 * - width% 注入一致
 *
 * 不再支持图片缩放交互（点击图片弹出面板），quiz 模式纯展示。
 */
export default function QuestionHtmlWithZoom({
  html,
  imageWidths = [],
  isPreview = false,
}: QuestionHtmlWithZoomProps) {
  const containerRef = useRef<HTMLDivElement>(null);

  // 统一处理 HTML（与编辑器走同一份函数）
  const processedHtml = useMemo(() => {
    return processQuestionHtmlForRender(html, imageWidths);
  }, [html, imageWidths]);

  // 提取图片 URL（用于判断是否有图片）
  const imageUrls = useMemo(() => extractImageUrls(html), [html]);

  // 图片高度限制：基于容器宽度动态计算 max-height（与编辑模式一致）
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    let cleanupFn: (() => void) | null = null;
    let cancelled = false;

    const rafId = requestAnimationFrame(() => {
      if (cancelled) return;
      try {
        cleanupFn = watchImageHeight(container);
      } catch (error) {
        console.warn('Failed to initialize image height observer:', error);
      }
    });

    return () => {
      cancelled = true;
      cancelAnimationFrame(rafId);
      if (cleanupFn) cleanupFn();
    };
  }, [html]);

  // 统一渲染：无论是否有图片，都用同一套处理逻辑
  // 静默 isPreview 参数（保留接口兼容性，但不再影响渲染）
  void isPreview;

  return (
    <div
      ref={containerRef}
      className="question-html-content max-w-none"
      style={{ whiteSpace: 'pre-line' }}
      dangerouslySetInnerHTML={{ __html: processedHtml }}
    />
  );
}
